import React, { useState } from 'react';
import { MessageCircleQuestionMark } from 'lucide-react';

import GlobalSearch from './GlobalSearch';
import SupportRequestDialog from './support/SupportRequestDialog';
import { Button } from './ui/button.jsx';
import { Tooltip } from './ui/tooltip.jsx';

/**
 * The controls that sit at the right of every page header.
 *
 * One component rather than a support button added page by page: `GlobalSearch` already
 * appears in ten headers, and anything that has to appear beside it in all ten belongs in the
 * same place. A page adds both by rendering this.
 *
 * The support control is an icon, not a labelled button — the header already carries a search
 * field, and a second piece of text on every page in the app is a poor trade for a feature
 * most people touch rarely. The label lives in the tooltip and in `aria-label`, so it is
 * still announced and still discoverable on hover. It sits to the left of the search, so the
 * search field is the header's right edge on every page.
 *
 * Anything else passed goes through to `GlobalSearch`, so a page that handles its own search
 * selections (Quotes does) reads the same as it did before this component existed.
 */
export default function PageHeaderActions({ className = '', searchClassName = 'w-72', ...searchProps }) {
  const [supportOpen, setSupportOpen] = useState(false);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Tooltip content="Get support">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Get support"
          onClick={() => setSupportOpen(true)}
        >
          <MessageCircleQuestionMark className="h-5 w-5" />
        </Button>
      </Tooltip>

      <GlobalSearch className={searchClassName} {...searchProps} />

      <SupportRequestDialog open={supportOpen} onOpenChange={setSupportOpen} />
    </div>
  );
}
